import React from 'react';
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '../../../ui/core';

/**
 * Candidate review: one row per `GeneratedCandidate`, plus the `SweepStats`
 * block for the sweep that produced them.
 *
 * Shapes are quoted from hitch-services `origin/main`,
 * `apps/admin/src/station-candidates.service.ts` +
 * `apps/admin/src/station-candidates.filters.ts`. Three things there are
 * contract rather than commentary, and this file is where they become visible
 * to an operator.
 *
 * 1. `stats.dropped` IS A `Record<string, number>`, NOT A FIXED-KEY OBJECT.
 *    It is built by a `countDrop(reason)` closure, so a key exists only when
 *    that reason actually fired during the sweep. Rendering it as fixed fields
 *    would print "0" for reasons that never fired (a claim nobody made) and,
 *    worse, would make any drop reason the backend adds later INVISIBLE — a
 *    silent loss of exactly the information this block exists to convey. Hence
 *    `Object.entries` over a label map with an unknown-key fallback.
 *
 * 2. `successfulRequestCount` IS NOT A BILLED-REQUEST COUNT, and the field's
 *    own doc comment says so: "deliberately NOT a billed-request count. The
 *    cache serves an unknown share of `requestCount`, so
 *    `successfulRequestCount` cannot be turned into spend without knowing the
 *    cache hit rate, which this sweep does not track." It was renamed FROM
 *    `actualRequestCount` precisely because that name implied spend and was
 *    wrong: re-sweeping an already-cached area reported near-100% of requests
 *    as "actual" spend when most were free cache hits. So it is labelled as
 *    requests that RETURNED DATA, never as cost, spend, or "billed". The only
 *    cache number reported anywhere in this feature is the ESTIMATE's
 *    `cachedRequestCount`, which is a real probe — and that lives in
 *    `SearchParamsPanel`, not here.
 *
 * 3. `malformedPlaceCount` SITS OUTSIDE `dropped` ON PURPOSE. Its doc comment:
 *    every reason in `dropped` partitions the candidates that DID reach
 *    `uniquePlaceCount`, and folding malformed places in "would break
 *    `uniquePlaceCount === candidates + sum(dropped)` for anyone doing
 *    arithmetic on the block". It is therefore rendered in its own section,
 *    never as another drop reason, and its copy points at THE ACTUAL CAUSE: a
 *    nonzero value means our field mask or Google's response shape has drifted
 *    (no place id, or non-numeric coordinates), NOT that the area is empty.
 *
 * A fourth, quieter trap: `GeneratedCandidate` carries BOTH `cityId` (a
 * Postgres uuid) and `cityMongoId` (24-hex). `StationBatchItemDto` validates
 * `cityMongoId` with `@IsMongoId()`, so a row built from `cityId` 400s every
 * time. Nothing here forwards `cityId`; the only city identity this component
 * touches is the human label (`cityName`/`cityState`).
 *
 * `partnerType: 'regular'` and `operational: false` are already set on every
 * candidate by the service. This component neither invents nor overrides them,
 * and does not display them: they are constants of the sweep, not judgements an
 * operator makes.
 */

/**
 * Wire drop-reason key → operator-facing label.
 *
 * The twelve keys that can appear: six `DropReason`s from `evaluatePlace`
 * (`businessStatus`, `rating`, `reviewCount`, `dineIn`, `excludedType`,
 * `hours`), the `'filtered'` fallback, and five counted directly in
 * `generate()` (`duplicatePlaceId`, `previouslyRejected`, `nearExistingStation`,
 * `outsideCorridor`, `noNearbyCity`).
 *
 * `filtered` is kept here but IS NOT REACHABLE against today's backend. The
 * call site is `countDrop(verdict.reason ?? 'filtered')`, and every one of the
 * six `{ kept: false }` returns in `evaluatePlace` supplies a `reason` — so the
 * `??` branch is dead. It stays in the map because a future rule that forgets
 * its reason would land on it, and an unlabelled key is worse than a vague one.
 * There is deliberately no test asserting `filtered` renders from a realistic
 * sweep: such a test would encode a fiction as behaviour.
 */
export const DROP_REASON_LABELS = {
  businessStatus: 'Closed or not operational',
  rating: 'Rating below the minimum',
  reviewCount: 'Too few reviews',
  dineIn: 'No confirmed dine-in seating',
  excludedType: 'Excluded place type',
  hours: 'Opening hours outside the window',
  filtered: 'Filtered (no reason reported)',
  duplicatePlaceId: 'Already a station or candidate',
  previouslyRejected: 'Rejected in an earlier review',
  nearExistingStation: 'Within the dedupe radius of a station',
  outsideCorridor: 'Too far from a lane corridor',
  noNearbyCity: 'No nearby city to file it under',
};

export const APPROVED = 'approved';
export const REJECTED = 'rejected';

/**
 * Google's `PRICE_LEVEL_*` enum, ordered cheapest-first, with the label an
 * operator reads.
 *
 * Kept as an ORDERED LIST rather than a map, because the only thing this page
 * does with `priceLevel` besides print it is sort by it, and an object's key
 * order is not a contract. `PRICE_LEVEL_UNSPECIFIED` is Google's "we do not
 * know", which is why it is absent here rather than sorted first: unknown is
 * unknown, and it sorts to the end with every other null (see `compareBy`).
 */
export const PRICE_LEVELS = [
  { value: 'PRICE_LEVEL_FREE', label: 'Free' },
  { value: 'PRICE_LEVEL_INEXPENSIVE', label: '$' },
  { value: 'PRICE_LEVEL_MODERATE', label: '$$' },
  { value: 'PRICE_LEVEL_EXPENSIVE', label: '$$$' },
  { value: 'PRICE_LEVEL_VERY_EXPENSIVE', label: '$$$$' },
];

const priceLevelRank = (value) => {
  const index = PRICE_LEVELS.findIndex((level) => level.value === value);
  return index === -1 ? null : index;
};

export const priceLevelLabel = (value) =>
  PRICE_LEVELS.find((level) => level.value === value)?.label ?? null;

/**
 * SORTING IS THIS PAGE'S JOB, BY DESIGN — not an omission being patched.
 *
 * The backend deliberately returns candidates in fan-out order and does not
 * rank them, on the stated reasoning that any single server-side order has to
 * decide whether a three-signal hotel outranks a better-rated cafe closer to
 * the lane, and that trade-off differs per sweep. Its half of the bargain is to
 * emit the signals; this list's half is to let an operator sort by them. Until
 * both halves exist the signals are simply invisible, so these controls are
 * what make that design implemented rather than merely intended.
 *
 * `'sweep'` is the default and is NOT a no-op default chosen for convenience:
 * it is the order the backend returned, and it stays available so an operator
 * can always get back to the unranked truth after sorting.
 *
 * `defaultDescending` is per-key because the useful direction differs: a higher
 * amenity score and a higher rating are better, a shorter distance is better.
 * Starting every column ascending would make the first click on "Amenities"
 * show the worst candidates.
 */
export const SORT_OPTIONS = [
  { key: 'sweep', label: 'Sweep order', value: null },
  {
    key: 'amenityScore',
    label: 'Amenities',
    value: (candidate) => candidate.amenityScore,
    defaultDescending: true,
  },
  {
    key: 'rating',
    label: 'Rating',
    value: (candidate) => candidate.rating,
    defaultDescending: true,
  },
  {
    key: 'userRatingCount',
    label: 'Reviews',
    value: (candidate) => candidate.userRatingCount,
    defaultDescending: true,
  },
  {
    key: 'distanceToPolylineMeters',
    label: 'Lane distance',
    value: (candidate) => candidate.distanceToPolylineMeters,
    defaultDescending: false,
  },
  {
    key: 'priceLevel',
    label: 'Price',
    value: (candidate) => priceLevelRank(candidate.priceLevel),
    defaultDescending: false,
  },
];

/**
 * UNKNOWN SORTS LAST IN BOTH DIRECTIONS, never as a zero and never as an
 * infinity.
 *
 * This is the same rule the rest of this feature already enforces in three
 * places: `rating` renders null as "Unrated" rather than 0.0, `dineIn` renders
 * null as "unknown" rather than "no", and the backend keeps a null rating out of
 * `minRating` comparisons entirely because "a null rating is UNKNOWN, not zero.
 * Coercing it would drop every newly-opened place." A sort that treated null as
 * 0 would bury exactly those places at the bottom of an ascending sort and
 * float them to the top of a descending one — inventing a judgement about a
 * place nobody has measured.
 *
 * Ties break on `placeId` so the order is total: without it, sorting a set where
 * half the rows share an amenity score of 2 gives a different arrangement on
 * every render, and rows move under the cursor mid-review.
 */
export const compareBy = (valueOf, descending) => (a, b) => {
  const left = valueOf(a);
  const right = valueOf(b);
  const leftKnown = typeof left === 'number' && Number.isFinite(left);
  const rightKnown = typeof right === 'number' && Number.isFinite(right);

  if (!leftKnown && !rightKnown) return String(a.placeId).localeCompare(String(b.placeId));
  if (!leftKnown) return 1;
  if (!rightKnown) return -1;
  if (left === right) return String(a.placeId).localeCompare(String(b.placeId));
  return descending ? right - left : left - right;
};

/** Display order only. Never mutates, never reorders `decisions`. */
export const sortCandidates = (candidates, sortKey, descending) => {
  const option = SORT_OPTIONS.find((entry) => entry.key === sortKey);
  if (!option?.value) return candidates;
  return [...candidates].sort(compareBy(option.value, descending));
};

/**
 * WHICH CANDIDATES CAN BE SUBMITTED AT ALL — a DTO fact, not a UI preference.
 *
 * `StationBatchItemDto` has `@IsString() @IsNotEmpty()` on BOTH `name` and
 * `address`, and `StationBatchCreateDto` wraps the array in
 * `@ValidateNested({ each: true })`. `ValidationPipe` therefore rejects THE WHOLE
 * REQUEST before the controller's per-row `try/catch` can run: one blank field
 * takes all 100 rows in its chunk down with it, with the same repeated error
 * against 100 rows and nothing naming the offender. Every retry fails
 * identically. `CandidateRejectionDto` has the same `@IsNotEmpty() name`, so one
 * unnamed veto loses up to 200 rejections — which the next sweep then
 * re-proposes.
 *
 * Blank values are reachable, not theoretical: `toPlaceCandidate` sets
 * `name: displayName.text ?? ''` and `address: formattedAddress ?? ''`, and
 * `evaluatePlace` filters on neither. This file even renders `'(unnamed place)'`
 * and `'(no address)'` for them.
 *
 * So they are excluded here (the decision controls are disabled and bulk actions
 * skip them) AND again in the page's pre-flight before the payload is built. Two
 * gates on purpose: this one keeps the operator from making a decision that
 * cannot be honoured, and the page's keeps a hand-built decision map from
 * poisoning a chunk.
 */
const present = (value) => typeof value === 'string' && value.trim().length > 0;
export const canCreate = (candidate) =>
  present(candidate?.name) && present(candidate?.address);
export const canReject = (candidate) => present(candidate?.name);
export const submittableFor = (decision, candidate) =>
  decision === APPROVED ? canCreate(candidate) : canReject(candidate);

const count = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';

/**
 * `rating` is `number | null` — the backend keeps null as UNKNOWN rather than
 * coercing it to 0, because "a null rating is UNKNOWN, not zero. Coercing it to
 * 0 would drop every newly-opened place." So a bare `.toFixed(1)` here would
 * throw on live data for exactly those places.
 *
 * Exported so the map's candidate tooltip reads the same string this row does:
 * the null-rating rule is a backend fact, and a second copy of it would be one
 * more place to forget that null is not zero.
 */
export const rating = (value, userRatingCount) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return typeof userRatingCount === 'number'
      ? `Unrated (${count(userRatingCount)} reviews)`
      : 'Unrated';
  }
  return `${value.toFixed(1)} (${count(userRatingCount)} reviews)`;
};

/** `distanceToPolylineMeters` is `number | null` — null means no lane was found. */
const distance = (meters) => {
  if (typeof meters !== 'number' || !Number.isFinite(meters)) return 'No lane nearby';
  return meters >= 1000 ? `${(meters / 1000).toFixed(2)} km from a lane` : `${Math.round(meters)} m from a lane`;
};

/** `dineIn` is `boolean | null`, and null is NOT false — it is unknown. */
const dineInLabel = (dineIn) => {
  if (dineIn === true) return 'Dine-in';
  if (dineIn === false) return 'No dine-in';
  return 'Dine-in unknown';
};

/**
 * `amenityScore` is 0-3: one point each for a food-ish secondary type,
 * `servesCoffee === true`, `servesBreakfast === true`.
 *
 * THE LABEL MUST NOT OVERCLAIM, and that is a constraint from the backend
 * design rather than a style choice. The Places API has NO containment
 * relationship between places, so a lobby coffee shop is a separate Place
 * indistinguishable from one across the street — this score therefore cannot
 * prove a hotel has a cafe in it, and calling it "has a cafe" would put a claim
 * on screen the data cannot support. It is a hint for a human, never a gate, and
 * the wording says so.
 */
const amenityLabel = (score) => {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 'Amenity signals unknown';
  return `${score}/3 amenity signals`;
};

const cityLabel = (candidate) =>
  [candidate.cityName, candidate.cityState].filter(Boolean).join(', ') || 'Unknown city';

const StatRow = ({ label, value, help }) => (
  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {label}
      {help ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {help}
        </Typography>
      ) : null}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

/**
 * The dropped-reason breakdown.
 *
 * Driven entirely off the object's own keys. Sorted by count descending so the
 * reason that actually shaped the result set reads first — an operator looking
 * at 4 candidates wants to know it was dine-in that took 204 of them.
 */
export const DroppedBreakdown = ({ dropped }) => {
  const entries = Object.entries(dropped || {}).sort(([, a], [, b]) => b - a);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No places were dropped by a filter.
      </Typography>
    );
  }

  return (
    <Box>
      {entries.map(([reason, dropCount]) => {
        const known = Object.prototype.hasOwnProperty.call(DROP_REASON_LABELS, reason);
        return (
          <StatRow
            key={reason}
            // The raw wire key is shown verbatim for an unrecognised reason.
            // A reason the backend added after this file was written must still
            // be READABLE and COUNTED here, even unlabelled — dropping it from
            // the display would hide the very number that explains a thin
            // result set.
            label={known ? DROP_REASON_LABELS[reason] : reason}
            help={known ? undefined : 'Unrecognised drop reason — added by the sweep since this page was written.'}
            value={count(dropCount)}
          />
        );
      })}
    </Box>
  );
};

const PLACE_CLASS_LABELS = { food: 'Food places', lodging: 'Hotels' };

/**
 * THE PER-CLASS DROP BREAKDOWN — the first thing anyone wants when a hotel
 * sweep returns nothing, because it says whether hotels were dropped on hours,
 * on rating, or never found at all.
 *
 * IT DOES NOT RECONCILE WITH `dropped`, AND THAT IS BY DESIGN, so this renders
 * as its own section with the arithmetic stated rather than as a second column
 * beside the flat list. `droppedByClass` is FILTER-STAGE ONLY: the later
 * pipeline drops (`duplicatePlaceId`, `previouslyRejected`,
 * `nearExistingStation`, `outsideCorridor`, `noNearbyCity`) are class-blind and
 * appear in flat `dropped` alone. So `sum(droppedByClass) <= sum(dropped)`
 * always, and anything that implied the two should tally — a shared header, a
 * side-by-side layout, a "total" row — would turn a correct number into a
 * bug report.
 *
 * The existing `uniquePlaceCount === candidates kept + sum(dropped)` invariant
 * is untouched, and it is still the flat list that satisfies it.
 *
 * A class with no drops at all is omitted rather than shown as zero, matching
 * `DroppedBreakdown`'s own rule: a key exists only when that reason fired, and
 * printing 0 states something nobody measured.
 */
export const DroppedByClassBreakdown = ({ droppedByClass }) => {
  const classes = Object.entries(droppedByClass || {}).filter(
    ([, reasons]) => Object.keys(reasons || {}).length > 0,
  );

  if (classes.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Dropped by filter, per place class
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Filter-stage drops only, so these total LESS than the list above — duplicates, previous
        rejections, nearby stations, corridor distance and missing cities are decided later and
        are not per-class. Use this to tell &ldquo;no hotels qualified&rdquo; from &ldquo;no
        hotels were found&rdquo;.
      </Typography>
      {classes.map(([placeClass, reasons]) => (
        <Box key={placeClass} sx={{ mb: 0.5 }}>
          <Typography variant="caption" fontWeight={600} display="block">
            {/* The raw wire key for an unrecognised class, same rule as an
                unrecognised drop reason: a class the backend adds later must
                still be readable and counted here. */}
            {PLACE_CLASS_LABELS[placeClass] ?? placeClass}
          </Typography>
          <DroppedBreakdown dropped={reasons} />
        </Box>
      ))}
    </Box>
  );
};

/**
 * @param candidateCount HOW MANY CANDIDATES THE SWEEP RETURNED, not how many are
 *   currently listed. The two diverge the moment a submit succeeds: settled rows
 *   are pruned from the live list while `stats` describes the sweep that produced
 *   them, and this block invites the operator to check
 *   `uniquePlaceCount === candidates kept + sum(dropped)` in so many words. Feeding
 *   it the live length turns that caption into false arithmetic (180 unique / 2
 *   kept / 162 dropped), so the count must come from the sweep, immutably.
 */
/**
 * @param requestedLaneCount how many lanes the sweep was ASKED to measure
 *                           against — the length of the `laneIds` it sent, kept
 *                           by the page at sweep time. Null for a sweep that
 *                           sent none (corridor filter off).
 */
export const SweepStatsBlock = ({ stats, candidateCount = 0, requestedLaneCount = null }) => {
  if (!stats) return null;

  const clippedFrom = stats.cellsBeforeCorridorClip;
  const clipReported = Number.isFinite(clippedFrom) && Number.isFinite(stats.cells);
  /**
   * A lane that was asked for but had no polyline near the box contributed
   * nothing to this sweep — it neither widened the corridor nor kept a single
   * candidate. Saying so is the point of this row: a shortfall is the operator's
   * cue that a ticked lane is not the reason a place survived (or didn't), which
   * is precisely the confusion that made a correct corridor filter look broken
   * when the map showed a different lane set from the one it measured.
   */
  const laneShortfall =
    Number.isFinite(requestedLaneCount) && Number.isFinite(stats.corridorRouteCount)
      ? requestedLaneCount - stats.corridorRouteCount
      : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Sweep stats
      </Typography>

      <StatRow
        label="Search cells"
        help={
          clipReported
            ? `Clipped to the selected lanes' corridor from ${count(clippedFrom)}.`
            : undefined
        }
        value={count(stats.cells)}
      />
      {Number.isFinite(stats.corridorRouteCount) ? (
        <StatRow
          label="Lanes measured against"
          help={
            laneShortfall > 0
              ? `Of ${count(requestedLaneCount)} selected. The other ${count(
                  laneShortfall,
                )} had no polyline near this box, so they neither widened the corridor nor kept anything.`
              : 'Selected lanes with a polyline near this box — the set the corridor filter used.'
          }
          value={count(stats.corridorRouteCount)}
        />
      ) : null}
      <StatRow
        label="Places requests"
        help="Cells × place types."
        value={count(stats.requestCount)}
      />
      <StatRow
        label="Requests that returned data"
        // Never "billed", never "spend" — see note 2 in this file's header.
        help="From cache or live. Not a billed-request count: the cache serves an unknown share of these and the sweep does not track the hit rate."
        value={count(stats.successfulRequestCount)}
      />
      <StatRow
        label="Requests that failed"
        help="Counts cell × type requests, not cells."
        value={count(stats.failedRequestCount)}
      />

      <Divider sx={{ my: 1 }} />

      <StatRow label="Places returned" value={count(stats.rawPlaceCount)} />
      <StatRow
        label="Unique places"
        help="After de-duplicating by place id. Filters below partition this number."
        value={count(stats.uniquePlaceCount)}
      />
      <StatRow label="Candidates kept" value={count(candidateCount)} />
      <StatRow
        label="Kept with unknown hours"
        help="Places data omits opening hours often; these are kept for a human to judge."
        value={count(stats.hoursUnknownCount)}
      />

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Dropped by filter
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Only reasons that actually fired are listed. Unique places = candidates kept + these.
      </Typography>
      <DroppedBreakdown dropped={stats.dropped} />

      <DroppedByClassBreakdown droppedByClass={stats.droppedByClass} />

      {/* Separate section, NOT a drop reason. See note 3 in the header: these
          places never reached uniquePlaceCount, so listing them among the drop
          reasons would break the block's arithmetic — and the cause is
          completely different from a filter decision. */}
      {stats.malformedPlaceCount > 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {`${count(stats.malformedPlaceCount)} unreadable Places results`}
          </Typography>
          <Typography variant="caption" display="block">
            Not a filter decision, and not counted above: these had no place id or non-numeric
            coordinates, so they never reached the unique-place stage. A nonzero count here means
            our field mask or Google&apos;s response shape has drifted — it does not mean the area
            is empty.
          </Typography>
        </Alert>
      ) : (
        <StatRow
          label="Unreadable Places results"
          help="Missing place id or coordinates. Nonzero would mean our field mask or Google's response shape has drifted, not that the area is empty."
          value={count(stats.malformedPlaceCount)}
        />
      )}
    </Box>
  );
};

const CandidateRow = ({ candidate, decision, onDecide, disabled }) => (
  <Box sx={{ py: 1.5 }}>
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {candidate.name || '(unnamed place)'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {candidate.address || '(no address)'}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Chip size="small" label={candidate.primaryType || 'no primary type'} />
          <Chip size="small" label={rating(candidate.rating, candidate.userRatingCount)} />
          <Chip size="small" label={dineInLabel(candidate.dineIn)} />
          <Chip
            size="small"
            label={amenityLabel(candidate.amenityScore)}
            color={candidate.amenityScore > 0 ? 'info' : 'default'}
          />
          {priceLevelLabel(candidate.priceLevel) ? (
            <Chip size="small" label={priceLevelLabel(candidate.priceLevel)} />
          ) : null}
          {/* Only the TRUE cases get a chip. `servesCoffee`/`servesBreakfast`
              are `boolean | null`, and Google leaves them null far more often
              than it sets them false — so "No coffee" would read as a measured
              absence for places nobody measured. Same null-is-unknown rule the
              rating and dine-in labels follow. */}
          {candidate.servesCoffee === true ? <Chip size="small" label="Serves coffee" /> : null}
          {candidate.servesBreakfast === true ? (
            <Chip size="small" label="Serves breakfast" />
          ) : null}
          {candidate.outdoorSeating ? <Chip size="small" label="Outdoor seating" /> : null}
          {candidate.hoursUnknown ? <Chip size="small" color="warning" label="Hours unknown" /> : null}
          <Chip size="small" label={distance(candidate.distanceToPolylineMeters)} />
          <Chip size="small" label={cityLabel(candidate)} />
        </Stack>
        {canCreate(candidate) ? null : (
          // Said on the row rather than only at submit time: the DTOs reject the
          // whole request over one blank field, so a decision made here could not
          // be honoured and the operator should not be invited to make it.
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {canReject(candidate)
              ? 'Cannot be created — missing address. Places returned no formatted address for this place.'
              : 'Cannot be created or rejected — missing name. Places returned no display name for this place.'}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={0.5}>
        <Button
          size="small"
          name={`approve:${candidate.placeId}`}
          variant={decision === APPROVED ? 'contained' : 'outlined'}
          color="success"
          disabled={disabled || !canCreate(candidate)}
          // Clicking the current decision clears it, so a mis-click is
          // recoverable without a separate "undo" control.
          onClick={() => onDecide?.(candidate.placeId, decision === APPROVED ? null : APPROVED)}
        >
          Approve
        </Button>
        <Button
          size="small"
          name={`reject:${candidate.placeId}`}
          variant={decision === REJECTED ? 'contained' : 'outlined'}
          color="error"
          disabled={disabled || !canReject(candidate)}
          onClick={() => onDecide?.(candidate.placeId, decision === REJECTED ? null : REJECTED)}
        >
          Reject
        </Button>
      </Stack>
    </Stack>
  </Box>
);

/**
 * Sort controls as a row of buttons rather than a select, so the active key and
 * its direction are both visible without opening anything — and so pressing the
 * active key flips direction, which is the affordance a sortable column header
 * has everywhere else.
 */
const SortControls = ({ sortKey, descending, onSort }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
      Sort by
    </Typography>
    {SORT_OPTIONS.map((option) => {
      const active = option.key === sortKey;
      return (
        <Button
          key={option.key}
          size="small"
          name={`sort:${option.key}`}
          variant={active ? 'contained' : 'outlined'}
          onClick={() => onSort(option)}
        >
          {/* The arrow only on the active key: showing one on every button
              would claim five simultaneous sort directions. */}
          {active && option.value ? `${option.label} ${descending ? '↓' : '↑'}` : option.label}
        </Button>
      );
    })}
  </Stack>
);

/**
 * @param candidates   `GeneratedCandidate[]` from `POST /admin/stations/candidates`,
 *                     passed through untouched.
 * @param stats        `SweepStats` from the same response, or null before a sweep.
 * @param decisions    `{ [placeId]: 'approved' | 'rejected' }`. Controlled by
 *                     the parent, which is the thing that has to turn approvals
 *                     into `StationBatchItemDto`s and rejections into
 *                     `CandidateRejectionDto`s — both keyed on `placeId`, which
 *                     is why `placeId` is the key here too.
 * @param onDecide     `(placeId, decision)` — decision is `'approved'`,
 *                     `'rejected'`, or `null` to clear.
 * @param onDecideAll  `(decision, placeIds)` — bulk approve-all / reject-all.
 *                     `placeIds` is every listed candidate, in order.
 * @param paramsDrifted
 *                     true when the search params on screen no longer match the
 *                     ones these candidates were swept with. BLOCKS SUBMISSION and
 *                     says why; the rows themselves stay reviewable, because
 *                     reviewing writes nothing and the candidates are unchanged
 *                     paid-for data. See `PARAM_DRIFT` in the page.
 * @param keptCandidateCount
 *                     how many candidates THE SWEEP returned. Must be supplied by
 *                     any caller that prunes `candidates` (the page prunes settled
 *                     rows after a submit) or the stats block's arithmetic lies —
 *                     see `SweepStatsBlock`. Falls back to the listed length for
 *                     callers that never prune.
 */
const CandidateReviewList = ({
  candidates = [],
  stats = null,
  decisions = {},
  onDecide,
  onDecideAll,
  isSubmitting = false,
  onSubmit,
  keptCandidateCount = null,
  paramsDrifted = false,
  requestedLaneCount = null,
}) => {
  const [sortKey, setSortKey] = React.useState('sweep');
  const [descending, setDescending] = React.useState(true);

  const onSort = (option) => {
    if (option.key === sortKey) {
      // Re-pressing the active key flips it. `'sweep'` has no direction to flip,
      // so it is inert rather than silently toggling a hidden bit.
      if (option.value) setDescending((prev) => !prev);
      return;
    }
    setSortKey(option.key);
    setDescending(!!option.defaultDescending);
  };

  // DISPLAY ORDER ONLY. `decisions` is keyed on `placeId`, never on position, so
  // re-sorting mid-review cannot move a judgement onto a different row — and the
  // bulk actions below read from this sorted list so "approve all" means what is
  // on screen, in the order it is on screen.
  const sorted = sortCandidates(candidates, sortKey, descending);

  const placeIds = sorted.map((candidate) => candidate.placeId);
  // Bulk actions skip what the DTOs would refuse, so "approve all" cannot quietly
  // arm a row that takes its whole chunk down (see `canCreate`).
  const creatableIds = sorted.filter(canCreate).map((candidate) => candidate.placeId);
  const rejectableIds = sorted.filter(canReject).map((candidate) => candidate.placeId);
  const unsubmittableCount = candidates.length - rejectableIds.length;
  const approvedCount = placeIds.filter((placeId) => decisions[placeId] === APPROVED).length;
  const rejectedCount = placeIds.filter((placeId) => decisions[placeId] === REJECTED).length;
  const undecidedCount = candidates.length - approvedCount - rejectedCount;

  return (
    <Box>
      <SweepStatsBlock
        stats={stats}
        candidateCount={
          Number.isFinite(keptCandidateCount) ? keptCandidateCount : candidates.length
        }
        requestedLaneCount={requestedLaneCount}
      />

      <Divider />

      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Candidates ({candidates.length})
          </Typography>
          <Button
            size="small"
            name="approve-all"
            disabled={creatableIds.length === 0 || isSubmitting}
            onClick={() => onDecideAll?.(APPROVED, creatableIds)}
          >
            Approve all
          </Button>
          <Button
            size="small"
            name="reject-all"
            disabled={rejectableIds.length === 0 || isSubmitting}
            onClick={() => onDecideAll?.(REJECTED, rejectableIds)}
          >
            Reject all
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {`${approvedCount} approved · ${rejectedCount} rejected · ${undecidedCount} undecided`}
        </Typography>

        {unsubmittableCount > 0 ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="caption">
              {`${count(unsubmittableCount)} candidate${unsubmittableCount === 1 ? ' has' : 's have'} no name, so ${unsubmittableCount === 1 ? 'it' : 'they'} can neither be created nor recorded as rejected — both DTOs require one. Places returned no display name; nothing here can supply it.`}
            </Typography>
          </Alert>
        ) : null}

        {candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No candidates to review. Run a sweep, or check the drop reasons above to see what the
            filters removed.
          </Typography>
        ) : null}

        {candidates.length > 0 ? (
          <SortControls sortKey={sortKey} descending={descending} onSort={onSort} />
        ) : null}

        {sorted.map((candidate, index) => (
          <React.Fragment key={candidate.placeId}>
            {index > 0 ? <Divider /> : null}
            <CandidateRow
              candidate={candidate}
              decision={decisions[candidate.placeId] ?? null}
              onDecide={onDecide}
              disabled={isSubmitting}
            />
          </React.Fragment>
        ))}

        {/*
          Said immediately above the submit button, which is the thing it takes
          away. The two remedies are both named because they are genuinely
          different: re-running spends again but sweeps the settings on screen,
          while restoring the settings costs nothing and brings this list back
          intact (the check compares the NORMALISED request, so putting the value
          back clears this by itself).
        */}
        {paramsDrifted ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">
              These candidates came from different search settings than the ones now in the panel,
              so creating them could add stations that fail the criteria on your screen. Re-run the
              sweep to search with the current settings, or restore the previous ones to submit
              this list. Nothing here is lost either way.
            </Typography>
          </Alert>
        ) : null}

        {onSubmit ? (
          <Button
            name="submit-review"
            variant="contained"
            sx={{ mt: 2 }}
            disabled={isSubmitting || paramsDrifted || approvedCount + rejectedCount === 0}
            onClick={() => onSubmit()}
          >
            {isSubmitting
              ? 'Submitting…'
              : `Create ${approvedCount} station${approvedCount === 1 ? '' : 's'}, reject ${rejectedCount}`}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default CandidateReviewList;
